import { Group } from '@mantine/core';
import { Pagination } from '@sportybot-web/components/pagination/pagination';
import { DeleteClipButton } from '@sportybot-web/features/clips/components/clip-card/common-components/delete-clip-button';
import { CustomClipCard } from '@sportybot-web/features/clips/components/clip-card/custom-clip-card/custom-clip-card';
import { ClipGrid } from '@sportybot-web/features/clips/components/clip-grid/clip-grid';
import { CustomClipGridFilters } from '@sportybot-web/features/clips/components/clip-grid/custom-clip-grid-filters';
import { customClipsSearchSchema } from '@sportybot-web/features/clips/schema/clips-search-schema';
import { UserCustomClipsSort } from '@sportybot-web/services/sporty-gateway/generated/graphql';
import { userCustomClipsOptions } from '@sportybot-web/services/sporty-gateway/queries';
import { createCustomClipSearchActions } from '@sportybot-web/utils/navigation/navigation-helper';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_authenticated/clips/my-custom-clips')({
  component: RouteComponent,
  validateSearch: customClipsSearchSchema,
  search: {
    middlewares: [
      stripSearchParams({
        sortOrder: UserCustomClipsSort.CREATED_AT,
        gamePk: undefined,
        description: '',
      }),
    ],
  },
});
/* eslint-enable perfectionist/sort-objects */

function RouteComponent() {
  const { description, gamePk, limit, page, sortOrder } = useSearch({ from: Route.id });
  const navigate = useNavigate({ from: Route.fullPath });

  const { data, error, isFetching, isLoading, refetch, status } = useQuery({
    ...userCustomClipsOptions({ description, gamePk, limit, page, sortOrder }),
    select: (data) => data.userCustomClips,
  });

  const total = data?.total ?? 0;

  const { changeDescription, changeGamePk, changeLimit, changePage, changeSort } =
    createCustomClipSearchActions((updater) => navigate({ replace: true, search: updater }));

  return (
    <ClipGrid
      data={data?.customClips ?? []}
      error={error}
      filters={
        <Group justify="space-between" w="100%">
          <CustomClipGridFilters
            description={description}
            gamePk={gamePk}
            onDescriptionChange={changeDescription}
            onGamePkChange={changeGamePk}
            onSortChange={changeSort}
            sortOrder={sortOrder}
          />
          <Pagination
            initialLoading={isLoading}
            limit={limit}
            loading={isFetching && !isLoading}
            onChangeLimit={changeLimit}
            onChangePage={changePage}
            page={page}
            selectLabel="Clips Per Page"
            selectOptions={[16, 32, 48]}
            total={total}
          />
        </Group>
      }
      refetch={refetch}
      renderCard={(clip) =>
        clip ? (
          <CustomClipCard
            clip={clip}
            extraActions={
              clip.id &&
              clip.description && (
                <DeleteClipButton clipId={parseInt(clip.id)} name={clip.description} />
              )
            }
            key={clip.id}
          />
        ) : undefined
      }
      status={status}
    />
  );
}
